'use memo';

import clsx from 'clsx';
import { useRef } from 'react';
import {
  AgxRow,
  AgxColumn,
  AgxBodyText,
  AgxLabel,
  PageStatusType,
  FormPage,
  PropertyAddress,
} from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { findLastIndex } from '../formUtils';
import './DesktopSidebar.scss';
import { FormType } from '@urbanx/agx.campaigns.client';
import {
  AgxSpacer,
  AgxTypography,
  Icon_CheckmarkOutline,
} from '@urbanx/agx.ui-components2';
import slugify from 'slugify';
interface DesktopSidebarProps {
  address: PropertyAddress;
  formTypeValue?: FormType;
  pages: FormPage[];
  currentPage: FormPage;
  setPage: (pageNumber: number) => void;
  pageValidation: (pageNumber: number) => Promise<{
    status: boolean;
    isNextPage?: boolean;
    isPreviousPage?: boolean;
  }>;
  updatePageValidationFlag: (flag: boolean) => void;
}

export const DesktopSidebar = ({
  address,
  formTypeValue,
  pages,
  currentPage,
  setPage,
  pageValidation,
  updatePageValidationFlag,
}: DesktopSidebarProps) => {
  const pageStatuses = useAppSelector(state => state.form.pageStatuses);

  const elementRef = useRef(null);

  const twoLineAddress = cleanTwoLineAddress(address);
  const [addressLineOne, addressLineTwo] = twoLineAddress;
  const formName = (formTypeValue?: FormType) => {
    switch (formTypeValue) {
      case FormType.ListingGenerator:
        return 'Agreement';
      case FormType.ListingSubmission:
        return 'Listing Submission';
      case FormType.ContractRequest:
        return 'Contract';
      case FormType.ContractExchange:
        return 'Contract Exchange';
      case FormType.ContractSubmission:
        return 'Contract Submission';
      case FormType.MarketingPackageGenerator:
        return 'Marketing';
      case FormType.MarketingPackageSubmission:
        return 'Marketing Submission';
      default:
        return '';
    }
  };

  const isTablet =
    ScreenSize() === Breakpoints.Tablet_Landscape ||
    ScreenSize() === Breakpoints.Tablet_Portrait;
  const filteredPages = pages
    ?.map((page, pageNumber) => {
      return {
        name: page.name,
        isActive: page.name === currentPage?.name,
        pageNumber: pageNumber,
        hasBreadcrumbs: page.hasBreadcrumbs,
      };
    })
    ?.filter(pageDetails => pageDetails.hasBreadcrumbs);

  const navigate = async (pageNumber: number) => {
    const validationResult = await pageValidation(pageNumber);
    if (validationResult.status) {
      if (validationResult.isNextPage || validationResult.isPreviousPage) {
        setPage(pageNumber);
        updatePageValidationFlag(false);
      }
      return;
    }
  };

  const pagesListContent = (
    <AgxColumn extraClasses="pageListColumnStyle">
      {filteredPages?.map(
        ({ isActive, name, pageNumber }, orderedPageNumber) => {
          const lastCompletedStep =
            findLastIndex(
              pageStatuses,
              p => p.status === PageStatusType.Complete
            ) ?? 0;
          const pageStatus =
            pageStatuses?.find(p => p.pageName === name)?.status ??
            PageStatusType.FutureDisabled;
          const progressCircleClasses = clsx(
            'agx-progress-item-circle',
            'large',
            isActive ? 'active' : pageStatus
          );
          const pageRowStatus =
            orderedPageNumber === lastCompletedStep + 1 &&
            pageStatus === PageStatusType.FutureDisabled
              ? PageStatusType.Future
              : pageStatus;
          const pageRowClasses = clsx(
            'pageRow',
            isActive ? 'active' : pageRowStatus
          );

          return (
            <div
              className={pageRowClasses}
              onClick={() => navigate(pageNumber)}
              key={`page-${pageNumber}`}
            >
              <AgxRow centered largeGap>
                <div className={progressCircleClasses}>
                  <div className="agx-progress-item-text">
                    <AgxSpacer
                      fill
                      contentAlignment="center"
                      contentJustification="center"
                    >
                      {(isActive ||
                        pageStatus === PageStatusType.Future ||
                        pageStatus === PageStatusType.FutureDisabled) && (
                        <AgxTypography
                          id={`step-${orderedPageNumber}`}
                          type="body_ui"
                          size="small"
                          color={'var(--text-primary-on-brand)'}
                        >
                          {orderedPageNumber + 1}
                        </AgxTypography>
                      )}
                      {!isActive && pageStatus === PageStatusType.Complete && (
                        <Icon_CheckmarkOutline color="var(--fg-brand-main)" />
                      )}
                    </AgxSpacer>
                  </div>
                </div>
                <AgxTypography
                  id={`lbl-${slugify(name)}`}
                  type={'body_ui'}
                  size={'medium'}
                  color={'var(--text-secondary-on-brand)'}
                >
                  {name}
                </AgxTypography>
              </AgxRow>
            </div>
          );
        }
      ) ?? <></>}
    </AgxColumn>
  );

  return (
    <div className="desktopSidebar" ref={elementRef}>
      <div className="sidebarHeader">
        <AgxTypography
          id={'sideBarHeader-formName'}
          type={'label'}
          size={'medium'}
          color="var(--text-secondary-on-brand)"
        >
          {formName(formTypeValue)}
        </AgxTypography>
        {twoLineAddress?.length > 0 &&
          addressLineOne !== 'Address undefined' && (
            <AgxTypography
              id={'sideBarHeader-formName'}
              type={'heading'}
              size={'2x-large'}
              color="var(--text-primary-on-brand)"
            >
              {addressLineOne},<br />
              {addressLineTwo}
            </AgxTypography>
          )}
      </div>
      <div className="sidebarContent">{pagesListContent}</div>
    </div>
  );
};
